import { useRouter } from 'next/router'
import {
  type FormEvent,
  type ReactElement,
  type ReactNode,
  useMemo,
} from 'react'
import { Controller, useForm } from 'react-hook-form'

import { FooterActions } from '@/modules/contato/FooterActions'
import {
  type Answers,
  ContactFormStep,
  type RendererParams,
} from '@/modules/contato/components/ContactFormStep/index'
import { ContactSupportInfo } from '@/modules/contato/components/ContactSupportInfo/index'
import { formDataAdjusted } from '@/modules/contato/formData'
import { ContatoLayout } from '@/modules/contato/layout/index'
import {
  type ContactPidFormData,
  createContactPidResolver,
} from '@/modules/contato/pages/identificacao/contactPidFormResolver'
import {
  Container,
  FormInfoContainer,
} from '@/modules/contato/pages/identificacao/styles'
import { useContactState } from '@/modules/contato/state'
import type { NextPageWithLayout } from '@/shared/page'
import { FormContainer } from '@/shared/ui/Form/FormContainer'
import { Select } from '@/shared/ui/Form/Select/index'
import { TextField } from '@/shared/ui/Form/TextField/index'
import { maskCNPJ, maskCPF } from '@/shared/validation/masks'
import { formatNationalInputPhoneNumber } from '@/shared/validation/phoneNumberValidation'

const roleQuestion = formDataAdjusted.questions.role

const allowedRoles = ['employee', 'admin'] as const
type AllowedRole = (typeof allowedRoles)[number]

const isAllowedRole = (value: unknown): value is AllowedRole =>
  allowedRoles.includes(value as AllowedRole)

const fallbackRoleValue: AllowedRole = 'employee'

const roleOptionNames = (roleQuestion?.options ?? [])
  .map((option) => option.optionName)
  .filter((optionName): optionName is string => Boolean(optionName))

const Contact: NextPageWithLayout = () => {
  const {
    formState,
    setFormState,
    setCancelModalOpen,
    cancelModalOpen,
    setCurrentStep,
  } = useContactState()

  const router = useRouter()

  const initialRoleValue =
    formState.role && roleOptionNames.includes(formState.role)
      ? formState.role
      : fallbackRoleValue

  const resolver = useMemo(
    () =>
      createContactPidResolver({
        role: initialRoleValue,
      }),
    [initialRoleValue],
  )

  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    watch,
    trigger,
  } = useForm<ContactPidFormData>({
    resolver,
    defaultValues: {
      cpf: maskCPF(formState.cpf),
      fullName: formState.fullName,
      email: formState.email,
      emailConfirmation: formState.emailConfirmation,
      phone: formatNationalInputPhoneNumber(formState.phone),
      role: initialRoleValue,
      companyName: formState.companyName,
      cnpj: formState.cnpj,
    },
  })

  const watchedRole = watch('role')

  const dependencyAnswers = useMemo<Answers>(
    () => ({
      role: watchedRole || fallbackRoleValue,
    }),
    [watchedRole],
  )

  const identificationRenderers = useMemo<
    Partial<
      Record<string, (params: RendererParams<ContactPidFormData>) => ReactNode>
    >
  >(
    () => ({
      cpf: ({ question }) => (
        <TextField
          type="text"
          label={question.questionLabel}
          autoComplete="on"
          error={errors.cpf?.message}
          placeholder="000.000.000-00"
          {...register('cpf', {
            onChange(event) {
              const { value } = event.target
              event.target.value = maskCPF(value)
            },
          })}
        />
      ),
      role: ({ question, context, selectOptions }) => {
        const controlRef = context.control ?? control
        if (!controlRef) return null

        return (
          <Controller
            control={controlRef}
            name="role"
            render={({ field }) => (
              <Select
                label={question.questionLabel}
                options={selectOptions}
                defaultValue={formState?.role || fallbackRoleValue}
                error={errors.role?.message}
                onValueChange={field.onChange}
                value={field.value}
                {...field}
              />
            )}
          />
        )
      },
      companyName: ({ question }) => (
        <TextField
          type="text"
          label={question.questionLabel}
          autoComplete="on"
          error={errors.companyName?.message}
          placeholder="Sua Empresa"
          {...register('companyName')}
        />
      ),
      cnpj: ({ question, answers }) => {
        const label =
          answers.role === 'admin'
            ? question.questionLabel
            : `${question.questionLabel} (opcional)`

        return (
          <TextField
            type="text"
            label={label}
            autoComplete="on"
            error={errors.cnpj?.message}
            placeholder="00.000.000/0000-00"
            {...register('cnpj', {
              onChange(event) {
                const { value } = event.target
                event.target.value = maskCNPJ(value)
              },
            })}
          />
        )
      },
      fullName: ({ question }) => (
        <TextField
          type="text"
          autoComplete="on"
          label={question.questionLabel}
          error={errors.fullName?.message}
          placeholder="Seu Nome"
          {...register('fullName')}
        />
      ),
      phone: ({ question }) => (
        <TextField
          type="tel"
          label={question.questionLabel}
          autoComplete="on"
          error={errors.phone?.message}
          placeholder="(00) 00000-0000"
          {...register('phone', {
            onChange(event) {
              const { value } = event.target
              event.target.value = formatNationalInputPhoneNumber(value)
            },
          })}
        />
      ),
      email: ({ question }) => (
        <TextField
          type="text"
          label={question.questionLabel}
          autoComplete="on"
          error={errors.email?.message}
          placeholder="seu.email@email.com"
          {...register('email')}
        />
      ),
      emailConfirmation: ({ question }) => (
        <TextField
          type="text"
          label={question.questionLabel}
          autoComplete="on"
          error={errors.emailConfirmation?.message}
          placeholder="seu.email@email.com"
          {...register('emailConfirmation')}
        />
      ),
    }),
    [control, errors, formState?.role, register],
  )

  function handleGoBack(event: FormEvent) {
    event.preventDefault()
    setCurrentStep((prev) => prev - 1)
    router.back()
  }

  async function handleNextStep(data: ContactPidFormData) {
    try {
      // Revalidate the form to clear any stale errors (especially helpful for autofilled fields)
      const isValid = await trigger()

      if (!isValid) {
        // If validation fails, don't proceed - let the user see the errors
        return
      }

      // Ensure proper formatting of fields that might be autofilled
      const formattedData = {
        ...data,
        cpf: maskCPF(data.cpf),
        phone: formatNationalInputPhoneNumber(data.phone),
        role: isAllowedRole(data.role) ? data.role : fallbackRoleValue,
      }

      setFormState((prevState) => ({
        ...prevState,
        ...formattedData,
      }))

      router.push('/contato/motivo-de-contato')
      setCurrentStep((prev) => prev + 1)
    } catch (error) {
      router.push('/contato/sinto-muito')
    }
  }

  return (
    <Container>
      <form onSubmit={handleSubmit(handleNextStep)}>
        <div>
          <FormInfoContainer>
            <ContactSupportInfo />
          </FormInfoContainer>

          <FormContainer>
            <fieldset>
              <div
                style={{
                  display: 'grid',
                  gap: '12px',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                }}
              >
                <ContactFormStep
                  questionNames={['cpf', 'role']}
                  answers={dependencyAnswers}
                  renderers={identificationRenderers}
                  context={{ control, register }}
                />
              </div>

              <ContactFormStep
                questionNames={['companyName']}
                answers={dependencyAnswers}
                renderers={identificationRenderers}
                context={{ control, register }}
              />

              <ContactFormStep
                questionNames={['cnpj']}
                answers={dependencyAnswers}
                renderers={identificationRenderers}
                context={{ control, register }}
              />

              <div
                style={{
                  display: 'grid',
                  gap: '12px',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                }}
              >
                <ContactFormStep
                  questionNames={['fullName', 'phone']}
                  answers={dependencyAnswers}
                  renderers={identificationRenderers}
                  context={{ control, register }}
                />
              </div>

              <div
                style={{
                  display: 'grid',
                  gap: '12px',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                }}
              >
                <ContactFormStep
                  questionNames={['email', 'emailConfirmation']}
                  answers={dependencyAnswers}
                  renderers={identificationRenderers}
                  context={{ control, register }}
                />
              </div>
            </fieldset>
          </FormContainer>
        </div>

        <FooterActions
          cancelModalOpen={cancelModalOpen}
          onCancelModalOpen={() => setCancelModalOpen((prev) => !prev)}
          onHandleGoBack={handleGoBack}
          primaryButtonDisabled={isSubmitting}
          loading={isSubmitting}
        />
      </form>
    </Container>
  )
}

Contact.getLayout = function getLayout(page: ReactElement) {
  return <ContatoLayout>{page}</ContatoLayout>
}

export default Contact
